// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useContext, useMemo } from "react";
import { useQuery } from "@apollo/client";
import { AuthContext } from "@/providers/AuthContextProvider";
import { useTenantStore } from "@/providers/TenantContextProvider";
import { GET_MESSAGING_ACCOUNTS, GET_MESSAGING_ACCOUNT_SUMMARIES, } from "@/queries/GetMessagingAccounts";
import { GET_MESSAGING_CALLBACK_STATUS, } from "@/queries/GetMessagingCallbackStatus";
import { withCallbackStatus } from "@/services/messaging";
import { IPermissions } from "@/types/keycloak";
import { roleContext, useMessagingReadRole } from "./useMessagingReadRole";
/**
 * The tenant's sending accounts. With messaging-account-read (Settings) they
 * include when each credential was replaced; otherwise only the safe columns
 * are read, with messaging-config-read. Without either, nothing is read and
 * `canRead` is false: the default role can't read accounts.
 *
 * `summaries` reads only the safe columns even with messaging-account-read,
 * for screens that need no credential records (least privilege).
 *
 * With `callbackStatus`, each account's check carries its callback status
 * (`messaging_callback_status`):
 * without a callback address, Messenger isn't ready for codes and receipts are
 * unavailable. When that status can't be read, the accounts come without it
 * and the backend's own validation still refuses what it blocks.
 */
export var useMessagingAccounts = function (options) {
    if (options === void 0) { options = {}; }
    var authContext = useContext(AuthContext);
    var tenantId = useTenantStore()[0];
    var canReadAccounts = authContext.isAuthorized(true, tenantId, IPermissions.MESSAGING_ACCOUNT_READ);
    var readRole = useMessagingReadRole();
    var withCredentials = canReadAccounts && !options.summaries;
    var role = withCredentials
        ? IPermissions.MESSAGING_ACCOUNT_READ
        : (readRole !== null && readRole !== void 0 ? readRole : (canReadAccounts ? IPermissions.MESSAGING_ACCOUNT_READ : null));
    var _a = useQuery(withCredentials ? GET_MESSAGING_ACCOUNTS : GET_MESSAGING_ACCOUNT_SUMMARIES, {
        variables: { tenantId: tenantId },
        skip: options.skip || !tenantId || !role,
        context: roleContext(role),
    }), data = _a.data, loading = _a.loading, error = _a.error, refetch = _a.refetch;
    var callbacks = useQuery(GET_MESSAGING_CALLBACK_STATUS, {
        skip: !options.callbackStatus || options.skip || !tenantId || !role,
        context: roleContext(role),
    }).data;
    var accounts = useMemo(function () {
        var _a, _b;
        return withCallbackStatus((_a = data === null || data === void 0 ? void 0 : data.sequent_backend_messaging_account) !== null && _a !== void 0 ? _a : [], (_b = callbacks === null || callbacks === void 0 ? void 0 : callbacks.messaging_callback_status) === null || _b === void 0 ? void 0 : _b.accounts);
    }, [data, callbacks]);
    return {
        accounts: accounts,
        loading: loading,
        error: error,
        refetch: refetch,
        canRead: !!role,
    };
};
